/**
 * Labstep
 *
 * @module models/folder
 * @desc Typescript export class for Folder
 */

import { Type } from 'class-transformer';
import type { PermissionActions } from 'labstep-web/models';
import { Entity } from 'labstep-web/models/entity.model';
import { EntityUser } from 'labstep-web/models/entity-user.model';
import { Group } from 'labstep-web/models/group.model';
import { Permission } from 'labstep-web/models/permission.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';

export enum FolderType {
  entity_view = 'entity_view',
  experiment_workflow = 'experiment_workflow',
  protocol_collection = 'protocol_collection',
}

export class Folder extends Entity {
  static readonly entityName = 'folder' as const;

  public static readonly idAttr: string = 'guid';

  get entityName(): typeof Folder.entityName {
    return Folder.entityName;
  }

  get displayEntityName(): string {
    return this.entityName;
  }

  constructor(data: Partial<Folder> = {}) {
    super();
    Object.assign(this, data);
  }

  id!: number;

  name!: string;

  entity_view_count!: number;

  protocol_collection_count!: number;

  experiment_workflow_count!: number;

  type!: FolderType;

  folder_path!: string;

  inner_folder_ids!: Folder['id'][];

  entity_users_count!: number;

  permissions_user_count!: number;

  is_sharing_enabled!: boolean;

  @Type(() => User)
  author!: User;

  @Type(() => Group)
  owner!: Group;

  @Type(() => EntityUser)
  entity_users_preview!: EntityUser[];

  @Type(() => Permission)
  permissions!: Permission[];

  @Type(() => Folder)
  outer_folder?: Folder;

  public allowed_actions!: PermissionActions[];

  get associatedEntitiesCount(): number {
    return this[`${this.type}_count`];
  }

  get isAssociated(): boolean {
    return this.associatedEntitiesCount > 0;
  }

  static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
  ): string {
    return getHumanReadableEntityName(
      this.entityName,
      plural,
      capitalized,
    );
  }

  get folderPathWithoutName(): string {
    return this.folder_path.split('>').slice(0, -1).join(' > ');
  }
}
